if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();

        this.modalContent = this.querySelector(
          '[id^="QuickAddInfo-"]'
        );

        this.addEventListener(
          'product-info:loaded',
          ({ target }) => {
            target.addPreProcessCallback(
              this.preprocessHTML.bind(this)
            );
          }
        );
      }

      lockPageScroll() {
        if (
          document.body.classList.contains(
            'lm-quick-add-open'
          )
        ) {
          return;
        }

        const scrollY =
          window.scrollY ||
          document.documentElement.scrollTop ||
          0;

        document.documentElement.dataset
          .lmQuickAddScrollY =
          String(scrollY);

        document.documentElement.classList.add(
          'lm-quick-add-open'
        );

        document.body.classList.add(
          'lm-quick-add-open'
        );

        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      }

      unlockPageScroll() {
        const storedScrollY =
          parseInt(
            document.documentElement.dataset
              .lmQuickAddScrollY || '0',
            10
          );

        document.documentElement.classList.remove(
          'lm-quick-add-open'
        );

        document.body.classList.remove(
          'lm-quick-add-open'
        );

        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';

        delete document.documentElement.dataset
          .lmQuickAddScrollY;

        window.scrollTo({
          top: storedScrollY,
          left: 0,
          behavior: 'instant'
        });
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector(
            'cart-notification'
          ) ||
          document.querySelector(
            'cart-drawer'
          );

        if (cartNotification) {
          cartNotification.setActiveElement(
            this.openedBy
          );
        }

        this.modalContent.innerHTML = '';

        if (preventFocus) {
          this.openedBy = null;
        }

        /*
         * Shopify schließt zuerst das Modal.
         * Danach wird der Scroll-Lock entfernt.
         */
        super.hide();

        this.unlockPageScroll();
      }

      show(opener) {
        opener.setAttribute(
          'aria-disabled',
          'true'
        );

        opener.classList.add('loading');

        const spinner =
          opener.querySelector(
            '.loading__spinner'
          );

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        const productUrl =
          opener.getAttribute(
            'data-product-url'
          );

        if (!productUrl) {
          opener.removeAttribute(
            'aria-disabled'
          );

          opener.classList.remove(
            'loading'
          );

          if (spinner) {
            spinner.classList.add('hidden');
          }

          console.error(
            'Quick Add: Es wurde keine Produkt-URL gefunden.'
          );

          return;
        }

        fetch(productUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Quick Add: Produkt konnte nicht geladen werden. Status: ${response.status}`
              );
            }

            return response.text();
          })
          .then((responseText) => {
            const responseHTML =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            const productElement =
              responseHTML.querySelector(
                'product-info'
              );

            if (!productElement) {
              throw new Error(
                'Quick Add: product-info wurde nicht gefunden.'
              );
            }

            this.preprocessHTML(
              productElement
            );

            HTMLUpdateUtility.setInnerHTML(
              this.modalContent,
              productElement.outerHTML
            );

            if (
              window.Shopify &&
              Shopify.PaymentButton
            ) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }

            this.modalContent
              .querySelector(
                'product-component'
              )
              ?.dispatchViewEvent?.();

            /*
             * Wichtig:
             * Shopify öffnet zuerst das Modal.
             * Erst danach wird die Hintergrundseite
             * gegen Scrollen gesperrt.
             */
            super.show(opener);

            this.lockPageScroll();
          })
          .catch((error) => {
            /*
             * Falls das Laden fehlschlägt, darf die
             * Seite keinesfalls gesperrt bleiben.
             */
            this.unlockPageScroll();

            console.error(error);
          })
          .finally(() => {
            opener.removeAttribute(
              'aria-disabled'
            );

            opener.classList.remove(
              'loading'
            );

            if (spinner) {
              spinner.classList.add(
                'hidden'
              );
            }
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach(
          (classApplied) => {
            if (
              classApplied.startsWith(
                'color-'
              ) ||
              classApplied === 'gradient'
            ) {
              this.modalContent.classList.add(
                classApplied
              );
            }
          }
        );

        this.preventDuplicatedIDs(
          productElement
        );

        this.removeDOMElements(
          productElement
        );

        this.removeGalleryListSemantic(
          productElement
        );

        this.updateImageSizes(
          productElement
        );

        this.preventVariantURLSwitching(
          productElement
        );
      }

      preventVariantURLSwitching(
        productElement
      ) {
        productElement.setAttribute(
          'data-update-url',
          'false'
        );
      }

      removeDOMElements(productElement) {
        const selectorsToRemove = [
          'pickup-availability',
          'product-modal',
          'modal-dialog',

          /*
          * Loox-Sterne direkt im Produktkopf
          */
          '.loox-rating',

          /*
          * Loox-Bewertungsbereiche
          */
          '.loox-reviews-default',
          '.loox-reviews',
          '[data-loox-reviews]',
          '[id*="looxReviews"]',
          '[class*="loox-review"]',

          /*
          * Loox Snippets Widget
          */
          'loox-snippets-widget',
          '.loox-widget',
          '.shopify-app-block:has(loox-snippets-widget)',
          '[id*="loox_reviews_loox_snippets_widget"]',

          /*
          * Steuer- und Versandhinweis
          */
          '.product__tax',
          '.product__tax.caption',
          '.product__tax.caption.rte',

          /*
          * Link „Vollständige Details anzeigen“
          */
          '.product__view-details'
        ];

        selectorsToRemove.forEach((selector) => {
          productElement
            .querySelectorAll(selector)
            .forEach((element) => {
              element.remove();
            });
        });
      }


      preventDuplicatedIDs(
        productElement
      ) {
        const sectionId =
          productElement.dataset.section;

        if (!sectionId) {
          return;
        }

        const oldId = sectionId;
        const newId =
          `quickadd-${sectionId}`;

        productElement.innerHTML =
          productElement.innerHTML.replaceAll(
            oldId,
            newId
          );

        Array.from(
          productElement.attributes
        ).forEach((attribute) => {
          if (
            attribute.value.includes(
              oldId
            )
          ) {
            productElement.setAttribute(
              attribute.name,
              attribute.value.replace(
                oldId,
                newId
              )
            );
          }
        });

        productElement.dataset.originalSection =
          sectionId;
      }

      removeGalleryListSemantic(
        productElement
      ) {
        const galleryList =
          productElement.querySelector(
            '[id^="Slider-Gallery"]'
          );

        if (!galleryList) {
          return;
        }

        galleryList.setAttribute(
          'role',
          'presentation'
        );

        galleryList
          .querySelectorAll(
            '[id^="Slide-"]'
          )
          .forEach((item) => {
            item.setAttribute(
              'role',
              'presentation'
            );
          });
      }

      updateImageSizes(productElement) {
        const product =
          productElement.querySelector(
            '.product'
          );

        const desktopColumns =
          product?.classList.contains(
            'product--columns'
          );

        if (!desktopColumns) {
          return;
        }

        const mediaImages =
          product.querySelectorAll(
            '.product__media img'
          );

        if (!mediaImages.length) {
          return;
        }

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (
          product.classList.contains(
            'product--medium'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '605px'
            );
        } else if (
          product.classList.contains(
            'product--small'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '495px'
            );
        }

        mediaImages.forEach((img) => {
          img.setAttribute(
            'sizes',
            mediaImageSizes
          );
        });
      }
    }
  );
}

